import { useCallback, useEffect, useState } from "react";
import { promptDialog } from "@/shared/ui";
import { useApp } from "../vault/ctx";
import { Thumb } from "../vault/Common";
import {
  listApprovals,
  listComments,
  addInternalComment,
  updateApproval,
  type Approval,
  type ApprovalStatus,
  type Comment,
} from "../../features/approvals/approvalsApi";
import { listMembers, type MembersResponse } from "../../features/team/teamApi";
import { getModel } from "../../features/vault/vaultApi";

function Mini({ art, bg, pin }: { art: string; bg: string; pin?: boolean }) {
  return <div className="kthumb" style={pin ? { overflow: "visible" } : undefined}>
    <Thumb art={art} bg={bg} className="thumb" style={{ position: "absolute", inset: 0, height: "100%" }} />
    {pin && <span className="pin" style={{ right: -8, top: -6 }}>1</span>}
  </div>;
}

/** Compact relative time. */
function ago(iso?: string): string {
  if (!iso) return "recently";
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)} m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} h ago`;
  const d = Math.floor(s / 86400);
  return d === 1 ? "yesterday" : `${d} d ago`;
}

const COLS: { status: ApprovalStatus; label: string; dot?: React.CSSProperties }[] = [
  { status: "awaiting", label: "Awaiting client", dot: { background: "var(--accent)", boxShadow: "0 0 0 3px var(--accent-soft)" } },
  { status: "changes", label: "Changes requested", dot: { background: "var(--red)", boxShadow: "0 0 0 3px var(--red-soft)" } },
  { status: "approved", label: "Approved" },
];

const userId = (u: { _id: string } | string) => (typeof u === "string" ? u : u._id);
const userName = (u: { name: string } | string) => (typeof u === "string" ? u : u.name);

export default function ApprovalsPage() {
  const { open, go, setModalData } = useApp();
  // undefined = loading, array = loaded (possibly empty).
  const [approvals, setApprovals] = useState<Approval[] | undefined>(undefined);
  const [comments, setComments] = useState<Record<string, Comment[]>>({});
  const [members, setMembers] = useState<MembersResponse | null>(null);

  const load = useCallback(async () => {
    const list = await listApprovals().catch(() => [] as Approval[]);
    setApprovals(list);
    const entries = await Promise.all(
      list.map(async (a) => [a._id, await listComments(a._id).catch(() => [])] as const),
    );
    setComments(Object.fromEntries(entries));
  }, []);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => { listMembers().then(setMembers).catch(() => setMembers(null)); }, []);

  const memberName = (id: string) => {
    const m = members?.members.find((mm) => userId(mm.userId) === id);
    return m ? userName(m.userId) : "member";
  };

  const reply = async (a: Approval) => {
    const body = await promptDialog({ title: "Reply to this approval's thread (internal)", label: "Comment" });
    if (!body?.trim()) return;
    await addInternalComment(a._id, body.trim());
    void load();
  };

  const assign = async (a: Approval) => {
    const ms = members?.members ?? [];
    if (!ms.length) { window.alert("No team members to assign."); return; }
    const menu = ms.map((m, i) => `${i + 1}. ${userName(m.userId)}`).join("\n");
    const pick = await promptDialog({ title: "Assign to team member", label: `Enter the number:\n${menu}`, defaultValue: "1" });
    if (!pick) return;
    const m = ms[parseInt(pick, 10) - 1];
    if (!m) return;
    await updateApproval(a._id, { assigneeId: userId(m.userId) });
    void load();
  };

  const toProduction = async (a: Approval) => {
    await updateApproval(a._id, { sentToProduction: true });
    void load();
  };

  const move = async (id: string, status: ApprovalStatus) => {
    const a = approvals?.find((x) => x._id === id);
    if (!a || a.status === status) return;
    await updateApproval(id, { status });
    void load();
  };

  // Open the real threaded model preview (file targets) or the real collection page
  // (collection targets) — never the hardcoded demo Preview/Compare modals.
  const view = async (a: Approval) => {
    if (a.targetFileId) {
      const m = await getModel(a.targetFileId).catch(() => null);
      if (m) { setModalData("preview", m); open("preview"); }
    } else if (a.targetCollectionId) {
      go("/collections/" + a.targetCollectionId);
    }
  };

  const loading = approvals === undefined;
  const grouped: Record<ApprovalStatus, Approval[]> = { awaiting: [], changes: [], approved: [] };
  for (const a of approvals ?? []) grouped[a.status]?.push(a);

  const acts = (a: Approval) => {
    if (a.status === "changes") return <>
      <button className="btn primary" onClick={() => reply(a)}>Reply</button>
      <button className="btn ghost" onClick={() => void view(a)}>View</button>
      <button className="btn ghost" onClick={() => assign(a)}>{a.assigneeId ? `Assigned: ${memberName(a.assigneeId)}` : "Assign"}</button>
    </>;
    if (a.status === "approved") return a.sentToProductionAt
      ? <button className="btn gold" disabled style={{ opacity: 0.6, cursor: "default" }}>✓ In production</button>
      : <button className="btn gold" onClick={() => toProduction(a)}>→ Send to production</button>;
    return <button className="btn ghost" onClick={() => void view(a)}>View</button>;
  };

  return (
    <>
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-start" }}>
        <div style={{ flex: 1, minWidth: 260 }}>
          <div className="page-title">Approvals</div>
          <div className="page-sub">Every design you've sent for sign-off, in one board. Clients approve, comment or request changes right on the share page — it lands here.</div>
        </div>
      </div>

      <div className="kanban">
        {COLS.map((col) => {
          const items = grouped[col.status];
          return (
            <div
              key={col.status}
              className="kcol"
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); void move(e.dataTransfer.getData("text/plain"), col.status); }}
            >
              <h4><span className="conn-dot" style={col.dot}></span> {col.label} <span className="kn">{loading ? "…" : items.length}</span></h4>
              {loading ? <div className="hint">Loading…</div>
                : items.length === 0 ? <div className="hint">Nothing yet</div>
                : items.map((a) => {
                  const cs = comments[a._id] ?? [];
                  const c = cs.find((x) => x.pinned) ?? cs[0];
                  return (
                    <div
                      key={a._id}
                      className="kcard"
                      draggable
                      onDragStart={(e) => e.dataTransfer.setData("text/plain", a._id)}
                    >
                      <div className="kc-top">
                        <Mini art="ringGold" bg="bgWarm" pin={cs.some((x) => x.pinned)} />
                        <div>
                          <b>{a.clientName || "Client"}</b>
                          <div className="kc-meta">
                            {a.status === "approved" ? <>✓ Approved · {ago(a.approvedAt ?? a.updatedAt)}{a.sentToProductionAt ? " · in production" : ""}</>
                              : a.status === "changes" ? <>{cs.some((x) => x.pinned) ? "1 pinned comment · " : ""}{ago(a.updatedAt)}</>
                              : <>sent {ago(a.sentAt ?? a.createdAt)} · {a.viewedAt ? "viewed" : "not viewed yet"}</>}
                          </div>
                        </div>
                      </div>
                      {c && <div className="kcomment"><b>{c.pinned ? "Pinned · " : ""}{c.authorName ?? "Team"}:</b> “{c.body}”</div>}
                      <div className="kc-acts">{acts(a)}</div>
                    </div>
                  );
                })}
            </div>
          );
        })}
      </div>
      <div className="anno" style={{ marginTop: 14 }}>ⓘ Approve / request-changes buttons live on the client share page — clients never need an account.</div>
    </>
  );
}
